import { withViewComponent } from '@updater-local/custom-element-v1';
import '@updater-components/cta';
import '../content-container';
import '../company-description';
import '../company-profile';
import '../company-logo';
import '../link';
import '../modal';
import '../toggle-selection';
import styles from './index.css';
import { when, either } from '../../utils';
import { remote } from '../../store/operations';
import { store } from '../../app/apollo-client-instance';
import { track } from '../../analytics';

export default class CompanyInfo extends withViewComponent(styles)() {
  static props = ['value', 'logoEffects', 'blankData', 'companySelection'];

  _state = {
    selected: null,
    modalOpen: false,
  };

  // TODO: Consider removing. Property is only used as a workaround b/c
  // hyperHTML + ShadowDOM interpolation of custom attributes breaks in IE11:
  // https://github.com/webcomponents/shadydom/issues/211
  set topValue(newValue) {
    newValue
      ? this.setAttribute('top-value', newValue)
      : this.removeAttribute('top-value');
  }

  _companySelectionHandler = event => {
    const { selected } = this._state;

    event.target.selected = !selected;

    this.dispatchEvent(
      new CustomEvent('companyselection', {
        bubbles: true,
        composed: true,
        detail: {
          company: this.value,
          selected: !selected,
        },
      })
    );
    this.mergeState({
      selected: !selected,
    });
  };

  updated({
    _state: { modalOpen, selected } = {},
    value,
    logoEffects,
    blankData,
    companySelection,
  }) {
    const { id, logo = {}, name, rating: { qualityRating } = {} } = value || {};

    const statuses = {
      REQUESTED: 'Estimate Request Sent',
      CANCELLED: 'Canceled',
    };

    const status =
      value.status && value.status !== 'inflight' ? statuses[value.status] : '';

    // if `selected` is null (hasn't been set yet)
    // use passed down isSelected to apply selected attr
    const isSelected =
      companySelection && selected === null
        ? companySelection.isSelected
        : selected || false;
    if (selected === null) {
      // syncronize selected state
      this.mergeState({
        selected: isSelected,
      });
    }
    const company = modalOpen
      ? store()
          .query(remote.moveIqCompanyDetail(value.id))
          .then(({ data: { moveIqCompany } }) => moveIqCompany)
      : Promise.resolve();

    this.render`
      <upd-moveiq-content-container
        disabled="${blankData}"
        hover="${blankData ? ' ' : ''}"
      >
        <div id="ribbon">Top Value</div>

        <upd-moveiq-company-logo
          id="logo"
          value="${logo}"
          logoEffects="${logoEffects}"
          alt="${`${name} Logo`}"
          onclick="${!blankData && this._handleViewProfile('Logo')}"
        >
        </upd-moveiq-company-logo>

        ${either(
          !blankData,
          () => this.wire()`
            <upd-moveiq-link 
              onclick="${this._handleViewProfile('Name')}"
              id="name"
            >
              ${name}
            </upd-moveiq-link>
          `,
          () => this.wire()`<div id="name">${name}</div>`
        )}
      </upd-moveiq-content-container>
      <upd-moveiq-company-description
        value="${value}"
        blankData="${blankData}"
      >
      </upd-moveiq-company-description>
      ${when(
        qualityRating,
        () => this.wire()`
        <div id="quality">
          <span>Quality Rating:</span>
          <span id="rating">${blankData ? 'N/A' : qualityRating}</span>
        </div>
      `
      )}
      ${when(
        !blankData,
        () => this.wire()`
        <upd-moveiq-link
          id="viewProfile"
          onclick="${this._handleViewProfile('Link')}"
        >
          View Profile and Reviews
        </upd-moveiq-link>
      `
      )}      
      ${when(
        !status && companySelection,
        () => this.wire()`
        <upd-moveiq-toggle-selection
          selected="${isSelected}"
          onclick="${this._companySelectionHandler}"
        >
        </upd-moveiq-toggle-selection>
        `
      )}
      ${when(
        status,
        () => this.wire()`
        <div class="pill" id="company-status">
          ${status}
        </div>
        `
      )}
      <upd-moveiq-modal
        open="${modalOpen}"
        onclose="${this._handleModalClose}"
        data-analytics-event="Company Profile"
        data-analytics-properties-company-id="${id}"
        data-analytics-on="close"
      >
        <upd-moveiq-loading
          overlay="true"
          id="loading"
          value="${company}"
        >
          <upd-moveiq-company-profile>
          </upd-moveiq-company-profile>
        </upd-moveiq-loading>
      </upd-moveiq-modal>
    `;

    /* 
      content-container checks for existance of the attribute for styling
      if blankData is true, we want to ensure that the hover attribute is not applied
      but hyperHtml will always render hover, with or without a value
      this works around hyperhtml by explicitly removing the attribute as per their suggestion
      https://github.com/WebReflection/hyperHTML/issues/87#issuecomment-322675465
    */
    const el = this.shadowRoot.querySelector('upd-moveiq-content-container');
    if (blankData) {
      el.removeAttribute('hover');
    }
  }

  _handleViewProfile = kind => e => {
    // allow clicks on container to raise events
    // allow clicks on name to be distinguished from container
    // and prevent propogation to avoid duplicate events
    e.stopPropagation();
    this.mergeState({ modalOpen: true });
    const args = [
      'Clicked',
      `View Profile ${kind}`,
      { companyId: this.value.id },
    ];
    this.dispatchEvent(track(...args));
  };

  _handleModalClose = () => {
    this.mergeState({ modalOpen: false });
  };
}

customElements.define('upd-moveiq-company-info', CompanyInfo);
